import { useEffect, useRef, useState } from "react";
import { useJobCheckpoints } from "@/hooks/useApi";
import type { JobMetadata, Service } from "@/types/api";
import { resetAvailability } from "./positionReset";

/** Job states in which checkpoints may still be saved. */
const ACTIVE_STATES = new Set(["running", "starting", "stopping"]);

/**
 * The job's checkpoints, read again every few seconds while it can still
 * save one. Starting and stopping still save checkpoints, and the jobs list
 * shows "stopping" the moment Stop is pressed, before the last save lands.
 */
export function useLiveCheckpoints(
  jobName: string,
  job: JobMetadata | undefined,
) {
  const active = ACTIVE_STATES.has(String(job?.state ?? "").toLowerCase());
  const { data, isLoading, refetch } = useJobCheckpoints(
    jobName,
    active ? 5000 : undefined,
  );
  // Polling ends once the job has stopped or failed, and its last save may
  // land after the last poll: read once more then.
  const wasActive = useRef(active);
  useEffect(() => {
    if (wasActive.current && !active) void refetch();
    wasActive.current = active;
  }, [active, refetch]);
  return { checkpoints: data, loading: isLoading };
}

/**
 * Whether the pipeline's position can be reset, and whether the dialog that
 * does it is open. Nothing is offered until the source's provider is known.
 */
export function useResetOffer(
  job: JobMetadata | undefined,
  services: Service[] | undefined,
) {
  const [open, setOpen] = useState(false);
  const provider = services?.find(
    (s) => s.name === job?.pipeline?.source.service_name,
  )?.provider;
  const offer =
    job && provider
      ? { ...resetAvailability(job, provider), onReset: () => setOpen(true) }
      : undefined;
  return { offer, open, close: () => setOpen(false) };
}
